import { normaliseResponse } from "@theme/normalise-response";
import { API_ENDPOINTS } from "@theme/api-endpoints";
import { getApiRequestContext } from "@theme/api-request-context";
import { getApiLocale } from "@theme/locale-map";

/**
 * APIM Store Search client module.
 *
 * Wraps the external Market America `store-search` API endpoint behind a
 * single async function that returns a normalised response envelope.
 *
 * Mirrors the `store-search` Postman call exactly:
 *   POST {API_ENDPOINTS.storeSearch}         (/shopify-app-proxy/store-search/v1/search)
 *   Headers: Content-Type: application/json, API_KEY: <api key>
 *   Body:    { from, query, size, siteType, languageCode, countryCode, page,
 *              weblevel, distributorInfo, shippingCountry, aggregations,
 *              filters, fields, sort, productType, eCommerceEnabled }
 *
 * Returns the same `{ ok, data, error }` envelope as `queryProductList` so
 * caller components (hydrateTypeAhead / hydratePLP / hydrateSLP) can share
 * the same success / zero-result / error handling patterns regardless of
 * whether they are listing products or stores.
 *
 * Usage:
 *   import { queryStoreList } from '@theme/query-store-list';
 *
 *   const result = await queryStoreList({ query: 'Nike', page: 1, size: 25 });
 *   if (result.ok) {
 *     renderStores(result.data.stores, result.data.totalCount);
 *   } else {
 *     showError(result.error.message);
 *   }
 */

/**
 * @typedef {Object} QueryStoreListParams
 * @property {string}        query              - Search term or query string. Pass '' for unfiltered browse.
 * @property {number}        [page]             - 1-based page number (default: 1).
 * @property {number}        [size]             - Results per page (default: 25). `limit` is accepted as an alias.
 * @property {number}        [from]             - Result offset. Defaults to (page - 1) * size.
 * @property {Object}        [filters]          - Filter map (e.g. { alphaSort: 'A', storeTypeList: 'Partner Store' }).
 * @property {Array<string>} [aggregations]     - Aggregation keys to request (default: []).
 * @property {Array<string>} [fields]           - Fields to return (default: basic store field set).
 * @property {string}        [sort]             - Sort key (default: 'BEST_MATCH'). Note: store-search uses
 *                                                 uppercase tokens — `BEST_MATCH`, `STORE_NAME_A_TO_Z`,
 *                                                 `STORE_NAME_Z_TO_A`, `MOST_CASHBACK`.
 * @property {string}        [siteType]         - Site type (default: 'SHP').
 * @property {string}        [languageCode]     - Defaults to the active Shopify language (ENG fallback).
 * @property {string}        [countryCode]      - Defaults to the active Shopify country (USA fallback).
 * @property {number}        [weblevel]         - Web level (default: 0).
 * @property {Array<string>} [productType]      - Product types to include (default: ['ONE','CPA','MA','WI']).
 * @property {string}        [eCommerceEnabled] - '0' or '1' (default: '1'). Sent as string per Postman.
 * @property {Object}        [distributorInfo]  - Distributor info block.
 * @property {string}        [shippingCountry]  - Defaults to the active countryCode.
 * @property {Object}        [extra]            - Additional top-level body keys.
 * @property {AbortSignal}   [signal]           - AbortSignal for request cancellation.
 */

/**
 * @typedef {Object} QueryStoreListData
 * @property {Array<Object>} stores       - The `hits` array from the APIM response (renamed for consumer clarity).
 * @property {number}        totalCount   - The `totalHits` value from the APIM response. (Note: store-search uses `totalHits`, NOT `totalResults` like product-search.)
 * @property {Object|null}   aggregations - The `aggregations` object (volumeType buckets, etc.) from the response, or null if absent.
 * @property {Object|null}   pagination   - The `pagination` object ({ page, totalPages }) from the response, or null if absent.
 * @property {Object}        raw          - The unmodified parsed JSON response, for consumers that need fields not surfaced above.
 *
 * @typedef {Object} QueryStoreListSuccess
 * @property {true}                 ok
 * @property {QueryStoreListData}   data
 * @property {null}                 error
 */

/**
 * @typedef {Object} QueryStoreListError
 * @property {false}  ok
 * @property {null}   data
 * @property {{ message: string, status: number | null }} error
 */

/** @typedef {QueryStoreListSuccess | QueryStoreListError} QueryStoreListResult */

/** Default request values mirroring the `store-search` Postman call. */
const DEFAULTS = {
  size: 25,
  siteType: "SHP",
  weblevel: 0,
  sort: "BEST_MATCH",
  productType: ["ONE", "CPA", "MA", "WI"],
  eCommerceEnabled: "1",
  shippingCountry: "",
  fields: [
    "volumeId",
    "ibvOnly",
    "catalogLogo",
    "ibvStandard",
    "convIbvStandard",
    "convIbvOnly",
    "channelbitsDelim",
    "wwwLink",
    "name",
    "derived",
    "labels",
    "rewards",
    "maStoreConditions",
    "productType",
    "address",
    "description",
    "walkIn",
    "venueId",
    "volumeName",
  ],
};

/**
 * The APIM store-search endpoint (same-origin App Proxy path). Centralized in
 * `@theme/api-endpoints` — formerly the `custom.apim_store_search_url` shop
 * metafield rendered into `Theme.routes`.
 *
 * @returns {string}
 */
const getEndpoint = () => API_ENDPOINTS.storeSearch;

/**
 * Reads the APIM API key configured via the `custom.apim_api_key` shop
 * metafield and rendered into `Theme.routes`. The same key is used for
 * both product-search and store-search.
 *
 * @returns {string}
 */
const getApiKey = () => {
  const routes = (typeof Theme !== "undefined" && Theme.routes) || {};
  return routes.apim_api_key || "";
};

/**
 * Builds the request headers. The API authenticates via the `API_KEY` header
 * (Azure APIM subscription credential) — NOT an Authorization bearer token.
 *
 * @param {string} apiKey
 * @returns {HeadersInit}
 */
const buildHeaders = apiKey => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  API_KEY: apiKey,
});

/**
 * Builds the request body matching the `store-search` Postman schema.
 *
 * @param {QueryStoreListParams} params
 * @returns {Object}
 */
const buildBody = params => {
  const { languageCode: localeLanguage, countryCode: localeCountry } = getApiLocale();
  const {
    query,
    page = 1,
    size = params.limit || DEFAULTS.size,
    from,
    filters = {},
    aggregations = [],
    fields = DEFAULTS.fields,
    sort = DEFAULTS.sort,
    siteType = DEFAULTS.siteType,
    languageCode = localeLanguage,
    countryCode = localeCountry,
    weblevel = DEFAULTS.weblevel,
    productType = DEFAULTS.productType,
    eCommerceEnabled = DEFAULTS.eCommerceEnabled,
    distributorInfo = {},
    shippingCountry = countryCode,
    extra = {},
  } = params;

  const context = getApiRequestContext(countryCode, siteType);
  const mergedDistributorInfo = { ...context.distributorInfo, ...distributorInfo };

  // siteType is deliberately left as asked for rather than switched to the distributor
  // catalogue, which holds far fewer stores — store search shows everyone the same ones.
  // distributorInfo still identifies the customer, so card rewards are unaffected.
  return {
    from: typeof from === "number" ? from : (page - 1) * size,
    query: typeof query === "string" ? query : "",
    size,
    siteType,
    languageCode,
    countryCode,
    page,
    weblevel,
    distributorInfo: mergedDistributorInfo,
    shippingCountry,
    aggregations,
    filters,
    fields,
    sort,
    productType,
    eCommerceEnabled,
    ...extra,
  };
};

/**
 * Queries the external APIM `store-search` endpoint.
 *
 * @param {QueryStoreListParams} params
 * @returns {Promise<QueryStoreListResult>}
 * @throws {DOMException} Re-throws AbortError so callers can distinguish
 *   cancellation from real failures.
 */
export async function queryStoreList(params = {}) {
  const { query, signal } = params;
  const endpoint = getEndpoint();
  const apiKey = getApiKey();

  // Unlike product-search, store-search allows an empty query string for
  // unfiltered browse (e.g. SLP showing all stores in a category). Only a
  // non-string query is invalid.
  if (typeof query !== "string") {
    return {
      ok: false,
      data: null,
      error: { message: "A search query string is required (empty string allowed).", status: null },
    };
  }

  if (!endpoint) {
    return {
      ok: false,
      data: null,
      error: {
        message: "APIM store search endpoint is not configured (@theme/api-endpoints).",
        status: null,
      },
    };
  }

  if (!apiKey) {
    return {
      ok: false,
      data: null,
      error: {
        message: "APIM API key is not configured (shop metafield custom.apim_api_key).",
        status: null,
      },
    };
  }

  const headers = buildHeaders(apiKey);
  const requestBody = buildBody(params);
  const body = JSON.stringify(requestBody);

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body,
      signal,
    });

    if (!response.ok) {
      let errorMessage = `Store search failed (${response.status})`;

      try {
        const errorJson = await response.json();
        if (errorJson.message) {
          errorMessage = errorJson.message;
        } else if (errorJson.error) {
          errorMessage =
            typeof errorJson.error === "string" ? errorJson.error : errorJson.error.message || errorMessage;
        }
      } catch {
        // Response body wasn't JSON — keep the generic message
      }

      return {
        ok: false,
        data: null,
        error: { message: errorMessage, status: response.status },
      };
    }

    const json = await response.json();

    const result = normaliseResponse(json, "store-list");
    // The exact request body sent to the API — analytics consumers must tag
    // request parameters with the same field names/values as the real call.
    result.data.request = requestBody;
    return result;
  } catch (error) {
    if (error.name === "AbortError") {
      throw error;
    }

    return {
      ok: false,
      data: null,
      error: {
        message: error.message || "An unexpected error occurred.",
        status: null,
      },
    };
  }
}
